// react
import { useCallback, useEffect, useMemo, useRef } from 'react';

// tanstack
import { useLocation, useNavigate, useSearch } from '@tanstack/react-router';

/**
 * A stable wrapper around TanStack Router's search params that prevents unnecessary navigations.
 * Provides a URLSearchParams-like API for compatibility with existing code.
 *
 * Uses TanStack Router's function-based `search` parameter in navigate() so that
 * each navigation reads the router's latest search state (updated synchronously
 * by commitLocation). This prevents race conditions when multiple hooks update
 * different search params in rapid succession.
 *
 * @returns {[URLSearchParams, function]} Tuple of [searchParams, setSearchParams]
 *
 * @example
 * const [searchParams, setSearchParams] = useSearchParams();
 * const nodeId = searchParams.get('node');
 *
 * // IMPORTANT: Always use the updater function pattern to avoid stale closures
 * setSearchParams((prev) => {
 *   const next = new URLSearchParams(prev);
 *   next.set('node', newNodeId);
 *   return next;
 * });
 *
 * @warning When extracting search params as dependencies:
 * - Extract PRIMITIVE values (strings, numbers) from searchParams, not the URLSearchParams object itself
 * - Use useMemo to memoize extracted primitives if needed to prevent unnecessary re-renders
 *
 * @example
 * // ❌ Bad: searchParams object changes on every unrelated param update
 * const nodeId = searchParams.get('node');
 * useEffect(() => {
 *   // This runs whenever ANY search param changes
 * }, [searchParams]);
 *
 * // ✅ Good: Depend on primitive values
 * const nodeId = searchParams.get('node');
 * useEffect(() => {
 *   // This only runs when nodeId changes
 * }, [nodeId]);
 *
 * // ✅ Also good: Memoize multiple params
 */
export default function useSearchParams() {
  const search = useSearch({ strict: false });
  const navigate = useNavigate();
  const location = useLocation();

  // Store pathname in a ref so setSearchParams reads the latest value
  // at call time without depending on it (keeping the callback stable).
  // This prevents downstream effects (e.g. useUrlDateRange) from re-firing
  // during route transitions.
  const pathnameRef = useRef(location.pathname);
  useEffect(() => {
    pathnameRef.current = location.pathname;
  }, [location.pathname]);

  // Convert TanStack Router's search object to URLSearchParams for compatibility
  const searchParams = useMemo(() => {
    const params = new URLSearchParams();
    if (search && typeof search === 'object') {
      Object.entries(search).forEach(([key, value]) => {
        if (value != null) {
          params.set(key, String(value));
        }
      });
    }
    return params;
  }, [search]);

  const setSearchParams = useCallback(
    (nextInit, navigateOptions) => {
      // Use TanStack Router's function-based search param so the router
      // provides its latest search state (via router.latestLocation, which
      // is updated synchronously by commitLocation). This prevents race
      // conditions when multiple hooks navigate in rapid succession.
      navigate({
        to: pathnameRef.current,
        search: (currentRouterSearch) => {
          // Convert router's current search object to URLSearchParams
          const currentParams = new URLSearchParams();
          if (currentRouterSearch && typeof currentRouterSearch === 'object') {
            Object.entries(currentRouterSearch).forEach(([key, value]) => {
              if (value != null) currentParams.set(key, String(value));
            });
          }

          if (typeof nextInit === 'function') {
            const prev = new URLSearchParams(currentParams);
            const result = nextInit(prev);

            // Updater returned prev unchanged — no update needed
            if (result === prev) return currentRouterSearch;

            return Object.fromEntries(new URLSearchParams(result).entries());
          }

          // Direct value case
          return Object.fromEntries(new URLSearchParams(nextInit).entries());
        },
        ...navigateOptions,
      });
    },
    [navigate],
  );

  return [searchParams, setSearchParams];
}
